import { create } from 'zustand';
import type { ElementType } from 'react';

/**
 * The POS Management sections, published by the POS Management page for the
 * sidebar to render in its place.
 *
 * The page owns which sections exist (role, module and location gates live
 * there); the sidebar only needs the resulting list and which one is open.
 * The list is deliberately not cleared when the page unmounts: a user editing
 * a product is still inside POS Management, and the sidebar should keep
 * showing its sections rather than flipping back to the main menu mid-task.
 */
export interface PosNavItem {
  key: string;
  label: string;
  icon: ElementType;
}

/** Sidebar grouping, in display order. A key not listed here still renders,
 *  appended to the last group, so a new section can never vanish. */
export const POS_NAV_GROUPS: Array<{ label: string; keys: string[] }> = [
  { label: 'Catalog', keys: ['products', 'departments', 'categories', 'modifiers', 'promotions', 'favorites'] },
  { label: 'Orders & stock', keys: ['purchase-orders', 'warehouse-orders', 'warehouse-outgoing', 'packing'] },
  { label: 'Customers', keys: ['customer-engagement'] },
  { label: 'Setup', keys: ['pos-config', 'mobile-config'] },
];

/** The URL that opens a section; tabs are deep-linkable as `?tab=<key>`. */
export function posSectionHref(key: string): string {
  return `#/pos-management?tab=${encodeURIComponent(key)}`;
}

interface PosNavStore {
  items: PosNavItem[];
  active: string | null;
  register: (items: PosNavItem[], active: string) => void;
}

export const usePosNavStore = create<PosNavStore>((set) => ({
  items: [],
  active: null,
  register: (items, active) => set({ items, active }),
}));
